import DownloadIcon from "@mui/icons-material/Download";
import { Box, Chip, Typography } from "@mui/material";
import { PropTypes } from "prop-types";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { Drawer } from "@/atoms";
import { distributionActionsData, statusTypesData } from "@/data";
import { useGetDistributionJob } from "@/hooks";
import { toggleDistributionDetailsDrawer } from "@/redux/actions";
import { StyledLink } from "@/styles";
import StyledChip from "@/styles/chip";
import { theme } from "@/theme";
import {
    convertDateToLocaleDateTimeFormatHandler,
    convertSecondsToFullDurationFormatHandler,
    getFileSizeHandler,
} from "@/utils";

// TODO(RCMS-1446): `getDistributionJob` does not expose `fileType` or `logFileSize` yet, so those fields fall back to
// N/A.
const DistributionDetailsDrawer = ({ distribution }) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const open = useSelector((state) => state.distributionsReducer?.openDetailsDrawer);

    const { data: distributionJob } = useGetDistributionJob(open ? distribution?.id : null);

    // `getDistributionJob` returns the raw job status (`pending` / `in_progress`), so keep the list's mapped status.
    const {
        client,
        createdOn,
        distributionAction,
        distributionType,
        fileType,
        finishedOn,
        logFilePath,
        logFileSize,
        packageSizeBytes,
        processedOn,
        releaseName,
        status,
        territory,
        trackIsrc,
        upc,
        videoFormat,
        videoIsrc,
    } = {
        ...distribution,
        ...(distributionJob?.id === distribution?.id && distributionJob),
        createdOn: distributionJob?.timestamp || distribution?.timestamp,
        distributionType: distribution?.distributionType,
        status: distribution?.status,
    };

    const notAvailable = t("labels.N/A");

    const duration =
        status === statusTypesData.active || !finishedOn || !processedOn
            ? notAvailable
            : convertSecondsToFullDurationFormatHandler(
                  (new Date(finishedOn).getTime() - new Date(processedOn).getTime()) / 1000,
              );

    const fieldHandler = (label, value) => ({
        label,
        value: value || notAvailable,
    });

    const actionFields = [
        fieldHandler(t("labels.createdOn"), convertDateToLocaleDateTimeFormatHandler(createdOn)),
        fieldHandler(t("labels.processedOn"), convertDateToLocaleDateTimeFormatHandler(processedOn)),
        fieldHandler(t("labels.finishedOn"), convertDateToLocaleDateTimeFormatHandler(finishedOn)),
        fieldHandler(t("labels.duration"), duration),
        fieldHandler(t("labels.distributedSize"), packageSizeBytes && getFileSizeHandler(packageSizeBytes)),
    ];

    const detailsFields = [
        fieldHandler(t("labels.trackIsrc"), trackIsrc),
        fieldHandler(t("labels.videoIsrc"), videoIsrc),
        fieldHandler(t("labels.releaseUpc"), upc),
        fieldHandler(t("labels.distributionType"), distributionType),
        fieldHandler(t("labels.territory"), territory?.name),
        fieldHandler(t("labels.videoFormat"), videoFormat?.toUpperCase()),
        // File type only applies to release distributions.
        ...(distributionAction !== distributionActionsData.video ? [fieldHandler(t("labels.fileType"), fileType)] : []),
    ];

    const renderFieldsHandler = (fields) => (
        <Box
            sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                    sm: "1fr 1fr",
                    xs: "1fr",
                },
            }}
        >
            {fields.map(({ label, value }) => (
                <Box key={label}>
                    <Typography sx={{ color: theme.palette.grey[600] }} variant="body2">
                        {label}
                    </Typography>
                    <Typography
                        sx={{
                            color: theme.palette.grey[700],
                            fontWeight: "bold",
                        }}
                        variant="body1"
                        component="span"
                    >
                        {value}
                    </Typography>
                </Box>
            ))}
        </Box>
    );

    const renderCardHandler = (heading, children) => (
        <Box
            sx={{
                background: theme.palette.grey[200],
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                p: 2,
            }}
        >
            {heading}
            {children}
        </Box>
    );

    return (
        <Drawer
            closeAction={() => dispatch(toggleDistributionDetailsDrawer(false))}
            open={!!(open && distribution)}
            titleComponent={
                <Box>
                    <Box
                        sx={{
                            alignItems: "center",
                            display: "flex",
                            gap: 1,
                        }}
                    >
                        <Typography component="h5" variant="h6">
                            {releaseName || notAvailable}
                        </Typography>
                        <StyledChip label={status} status={status} />
                    </Box>
                    <Typography sx={{ color: theme.palette.grey[600] }} variant="body2">
                        {[client?.name, territory?.name].filter(Boolean).join(" · ") || notAvailable}
                    </Typography>
                </Box>
            }
            isMini
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    mt: 2,
                    mx: 2,
                }}
            >
                {renderCardHandler(
                    <Typography
                        sx={{
                            color: theme.palette.grey[700],
                            fontWeight: "bold",
                            letterSpacing: 1,
                            textTransform: "uppercase",
                        }}
                        variant="caption"
                    >
                        {t("distributions.thisAction")}
                    </Typography>,
                    <>
                        <Box
                            sx={{
                                display: "flex",
                                gap: 1,
                            }}
                        >
                            {!!distributionType && (
                                <Chip
                                    label={distributionType}
                                    size="small"
                                    sx={{
                                        background: theme.palette.grey[300],
                                        borderRadius: 1.5,
                                        color: theme.palette.grey[600],
                                        fontWeight: "bold",
                                    }}
                                />
                            )}
                            <StyledChip label={status} status={status} />
                        </Box>
                        {renderFieldsHandler(actionFields)}
                        {!!client?.isApple && !!logFilePath && (
                            <StyledLink target="_blank" to={`${import.meta.env.VITE_BACKEND_DOMAIN}${logFilePath}`}>
                                <Box
                                    sx={{
                                        alignItems: "center",
                                        display: "flex",
                                        gap: 1,
                                    }}
                                >
                                    <DownloadIcon fontSize="small" />
                                    {logFileSize
                                        ? `${t("releases.downloadLogFile")} (${getFileSizeHandler(logFileSize)})`
                                        : t("releases.downloadLogFile")}
                                </Box>
                            </StyledLink>
                        )}
                    </>,
                )}
                {renderCardHandler(
                    <Typography
                        sx={{
                            color: theme.palette.grey[800],
                            fontWeight: "bold",
                        }}
                        variant="subtitle2"
                    >
                        {t("distributions.distributionDetails")}
                    </Typography>,
                    renderFieldsHandler(detailsFields),
                )}
            </Box>
        </Drawer>
    );
};

export default DistributionDetailsDrawer;

DistributionDetailsDrawer.propTypes = { distribution: PropTypes.object };
